import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../../components/Common/Card';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import Textarea from '../../components/Common/Textarea';
import Button from '../../components/Common/Button';
import api from '../../utils/api';
import { showSuccess, showError } from '../../utils/swal';
import { ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import RichTextEditor from '../../components/Common/RichTextEditor';
import FileUpload from '../../components/Common/FileUpload';

const PortfolioForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { office } = useAuth();
  const [loading, setLoading] = useState(false);
  const isEditMode = !!id;
  const [projects, setProjects] = useState([]);
  const [projectBlocks, setProjectBlocks] = useState([]);
  const [canEdit, setCanEdit] = useState(true);

  // Dinamik opsiyonlar (API'den gelir)
  const [options, setOptions] = useState({
    property_types: [],
    residential_types: [],
    listing_types: [],
    sale_types: [],
    portfolio_statuses: [],
    room_definitions: [],
    facade_types: [],
    cities: [],
  });
  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    property_type_id: '',
    residential_type_id: '',
    listing_type_id: '',
    sale_type_id: '',
    room_definition_id: '',
    facade_type_id: '',
    project_id: '',
    project_block_id: '',
    gross_sqm: '',
    net_sqm: '',
    floor: '',
    flat_number: '',
    building_age: '',
    status_id: '',
    city_id: '',
    district_id: '',
    neighborhood_id: '',
    address_detail: '',
    initial_price: '',
    list_price: '',
    commission_rate: '',
    additional_info: '',
    visibility: office?.settings?.default_portfolio_visibility === 'office' ? 'shared' : 'private',
    cover_photo: '',
    cover_photo_url: '',
  });

  useEffect(() => {
    fetchOptions();
    fetchProjects();
    if (isEditMode) {
      fetchPortfolio();
    }
  }, [id]);

  // Proje blokları — proje seçildiğinde
  useEffect(() => {
    if (formData.project_id) {
      fetchProjectBlocks(formData.project_id);
    } else {
      setProjectBlocks([]);
    }
  }, [formData.project_id]);

  // İlçeler — il seçildiğinde
  useEffect(() => {
    if (formData.city_id) {
      fetchDistricts(formData.city_id);
    } else {
      setDistricts([]);
      setFormData(prev => ({ ...prev, district_id: '', neighborhood_id: '' }));
    }
  }, [formData.city_id]);

  // Mahalleler — ilçe seçildiğinde
  useEffect(() => {
    if (formData.district_id) {
      fetchNeighborhoods(formData.district_id);
    } else {
      setNeighborhoods([]);
      setFormData(prev => ({ ...prev, neighborhood_id: '' }));
    }
  }, [formData.district_id]);

  // Opsiyonlar yüklendiğinde varsayılan değerleri set et
  useEffect(() => {
    if (!isEditMode && options.property_types.length > 0 && !formData.property_type_id) {
      const defaultPropertyType = options.property_types.find(t => t.code === 'residential');
      const defaultListingType = options.listing_types.find(t => t.code === 'sale');
      const defaultSaleType = options.sale_types.find(t => t.code === 'new');
      const defaultStatus = options.portfolio_statuses.find(t => t.code === 'active');

      setFormData(prev => ({
        ...prev,
        property_type_id: defaultPropertyType ? defaultPropertyType.id : '',
        listing_type_id: defaultListingType ? defaultListingType.id : '',
        sale_type_id: defaultSaleType ? defaultSaleType.id : '',
        status_id: defaultStatus ? defaultStatus.id : '',
      }));
    }
  }, [options]);

  const fetchOptions = async () => {
    try {
      const response = await api.get('/portfolio-options');
      setOptions(response.data);
    } catch (error) {
      console.error('Opsiyonlar yüklenemedi:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Projeler yüklenemedi:', error);
    }
  };

  const fetchPortfolio = async () => {
    try {
      const response = await api.get(`/portfolios/${id}`);
      const portfolio = response.data;
      setCanEdit(!portfolio.sales || portfolio.sales.length === 0 && !portfolio.rentals || portfolio.rentals.length === 0);
      setFormData({
        title: portfolio.title || '',
        property_type_id: portfolio.property_type_id || '',
        residential_type_id: portfolio.residential_type_id || '',
        listing_type_id: portfolio.listing_type_id || '',
        sale_type_id: portfolio.sale_type_id || '',
        room_definition_id: portfolio.room_definition_id || '',
        facade_type_id: portfolio.facade_type_id || '',
        project_id: portfolio.project_id || '',
        project_block_id: portfolio.project_block_id || '',
        gross_sqm: portfolio.gross_sqm || '',
        net_sqm: portfolio.net_sqm || '',
        floor: portfolio.floor || '',
        flat_number: portfolio.flat_number || '',
        building_age: portfolio.building_age || '',
        status_id: portfolio.status_id || '',
        city_id: portfolio.city_id || '',
        district_id: portfolio.district_id || '',
        neighborhood_id: portfolio.neighborhood_id || '',
        address_detail: portfolio.address_detail || '',
        initial_price: portfolio.initial_price || '',
        list_price: portfolio.list_price || '',
        commission_rate: portfolio.commission_rate || '',
        additional_info: portfolio.additional_info || '',
        visibility: portfolio.visibility || (office?.settings?.default_portfolio_visibility === 'office' ? 'shared' : 'private'),
        cover_photo: portfolio.cover_photo || '',
        cover_photo_url: portfolio.cover_photo_url || '',
      });

      // Edit modunda il/ilçe/mahalle yüklenmesi
      if (portfolio.city_id) {
        fetchDistricts(portfolio.city_id);
      }
      if (portfolio.district_id) {
        fetchNeighborhoods(portfolio.district_id);
      }
    } catch (error) {
      console.error('Portföy yüklenemedi:', error);
      showError('Portföy yüklenemedi');
      navigate('/portfolios');
    }
  };

  const fetchProjectBlocks = async (projectId) => {
    try {
      const response = await api.get(`/projects/${projectId}`);
      setProjectBlocks(response.data.blocks || []);
    } catch (error) {
      console.error('Bloklar yüklenemedi:', error);
    }
  };

  const fetchDistricts = async (cityId) => {
    try {
      const response = await api.get('/districts', { params: { city_id: cityId } });
      setDistricts(response.data);
    } catch (error) {
      console.error('İlçeler yüklenemedi:', error);
    }
  };

  const fetchNeighborhoods = async (districtId) => {
    try {
      const response = await api.get('/neighborhoods', { params: { district_id: districtId } });
      setNeighborhoods(response.data);
    } catch (error) {
      console.error('Mahalleler yüklenemedi:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // İl değiştiğinde ilçe ve mahalleyi sıfırla
    if (name === 'city_id') {
      setFormData(prev => ({
        ...prev,
        city_id: value,
        district_id: '',
        neighborhood_id: '',
      }));
      return;
    }

    // İlçe değiştiğinde mahalleyi sıfırla
    if (name === 'district_id') {
      setFormData(prev => ({
        ...prev,
        district_id: value,
        neighborhood_id: '',
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDescriptionChange = (content) => {
    setFormData(prev => ({
      ...prev,
      additional_info: content
    }));
  };

  const handleCoverPhotoUpload = async (file) => {
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('files[]', file);

      // Mevcut image upload endpoint'ini kullanabiliriz veya yeni bir tane oluşturulabilir.
      // Şimdilik mevcut olanı (upload-images) kullanıp ilk dosyanın yolunu alıyoruz.
      // Edit modunda ise id var, değilse geçici bir upload mekanizması gerekebilir.
      // Ancak backend'de generic bir upload controller var mı bakalım.
      
      const response = await api.post('/portfolios/upload-images-temp', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const path = response.data.files[0].path;
        const url = response.data.files[0].url;
        setFormData(prev => ({
          ...prev,
          cover_photo: path,
          cover_photo_url: url
        }));
      }
    } catch (error) {
      console.error('Kapak fotoğrafı yüklenemedi:', error);
      showError('Kapak fotoğrafı yüklenirken bir hata oluştu');
    }
  };

  const handleCoverPhotoDelete = () => {
    setFormData(prev => ({
      ...prev,
      cover_photo: '',
      cover_photo_url: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) {
      showError('Satılmış veya kiralanmış portföy düzenlenemez. İşlemi iptal ettikten sonra düzenleyebilirsiniz.');
      return;
    }
    setLoading(true);

    try {
      if (isEditMode) {
        await api.put(`/portfolios/${id}`, formData);
        showSuccess('Portföy başarıyla güncellendi');
      } else {
        await api.post('/portfolios', formData);
        showSuccess('Portföy başarıyla eklendi');
      }
      navigate('/portfolios');
    } catch (error) {
      console.error('Portföy eklenirken hata:', error);
      showError('Portföy kaydedilirken bir hata oluştu');
      setLoading(false);
    }
  };

  // Helper: code ile opsiyon bul
  const getOptionByCode = (optionList, code) => {
    return optionList.find(o => o.code === code);
  };

  // Seçili opsiyonların code'ları (kademeli kontrol için)
  const selectedPropertyType = options.property_types.find(t => t.id == formData.property_type_id);
  const selectedSaleType = options.sale_types.find(t => t.id == formData.sale_type_id);

  return (
    <div>
      <div className="mb-6">
        <Button variant="secondary" size="sm" onClick={() => navigate('/portfolios')}>
          <ArrowLeft size={16} />
          Geri
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        {!canEdit && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">
              Bu portföy satılmış veya kiralanmış durumda olduğu için düzenlenemez. İşlemi iptal ettikten sonra düzenleyebilirsiniz.
            </p>
          </div>
        )}
        <Card title="Kapak Fotoğrafı" className="mb-6">
          <div className="max-w-xl">
            <FileUpload
              label="Portföy Kapak Fotoğrafı"
              description="Portföy listelerinde ve paylaşım linklerinde görünecek ana görsel. (Max 10MB)"
              currentFile={formData.cover_photo_url}
              onUpload={handleCoverPhotoUpload}
              onDelete={handleCoverPhotoDelete}
              accept="image/*"
            />
          </div>
        </Card>

        <Card title="Görünürlük ve Paylaşım" className="mb-6">
          <div className="max-w-md">
            <Select
              label="Portföy Paylaşım Durumu"
              name="visibility"
              value={formData.visibility}
              onChange={handleChange}
              options={[
                { value: 'shared', label: 'Paylaşımlı — Herkes paylaşabilir' },
                { value: 'private', label: 'Sadece Ben — İzin gerektirir' },
                { value: 'draft', label: 'Taslak — Kimse göremez' },
              ]}
              required
            />
          </div>
        </Card>

        <Card title="Temel Bilgiler" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Portföy Başlığı"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Örn: 3+1 Deniz Manzaralı Daire"
              required
              className="md:col-span-2"
            />
            <Select
              label="Emlak Tipi"
              name="property_type_id"
              value={formData.property_type_id}
              onChange={handleChange}
              options={options.property_types.map(t => ({ value: t.id, label: t.name }))}
              required
            />
            {selectedPropertyType?.code === 'residential' && (
              <Select
                label="Konut Tipi"
                name="residential_type_id"
                value={formData.residential_type_id}
                onChange={handleChange}
                options={options.residential_types.map(t => ({ value: t.id, label: t.name }))}
              />
            )}
            <Select
              label="İlan Tipi"
              name="listing_type_id"
              value={formData.listing_type_id}
              onChange={handleChange}
              options={options.listing_types.map(t => ({ value: t.id, label: t.name }))}
              required
            />
            <Select
              label="Satış Tipi"
              name="sale_type_id"
              value={formData.sale_type_id}
              onChange={handleChange}
              options={options.sale_types.map(t => ({ value: t.id, label: t.name }))}
            />
            <Select
              label="Durum"
              name="status_id"
              value={formData.status_id}
              onChange={handleChange}
              options={options.portfolio_statuses.map(t => ({ value: t.id, label: t.name }))}
              required
            />
          </div>
        </Card>

        {selectedSaleType?.code === 'new' && (
          <Card title="Proje Bilgileri" className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Proje"
                name="project_id"
                value={formData.project_id}
                onChange={handleChange}
                options={projects.map(p => ({ value: p.id, label: p.name }))}
              />
              {projectBlocks.length > 0 && (
                <Select
                  label="Blok"
                  name="project_block_id"
                  value={formData.project_block_id}
                  onChange={handleChange}
                  options={projectBlocks.map(b => ({ value: b.id, label: b.name }))}
                />
              )}
            </div>
          </Card>
        )}

        <Card title="Özellikler" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Oda Sayısı"
              name="room_definition_id"
              value={formData.room_definition_id}
              onChange={handleChange}
              options={options.room_definitions.map(t => ({ value: t.id, label: t.name }))}
            />
            <Input
              label="Brüt Metrekare (m²)"
              name="gross_sqm"
              type="number"
              value={formData.gross_sqm}
              onChange={handleChange}
              placeholder="Örn: 165"
            />
            <Input
              label="Net Metrekare (m²)"
              name="net_sqm"
              type="number"
              value={formData.net_sqm}
              onChange={handleChange}
              placeholder="Örn: 145"
            />
            <Input
              label="Kat"
              name="floor"
              type="number"
              value={formData.floor}
              onChange={handleChange}
              placeholder="Örn: 5"
            />
            <Input
              label="Daire No"
              name="flat_number"
              value={formData.flat_number}
              onChange={handleChange}
              placeholder="Örn: 12"
            />
            <Select
              label="Cephe"
              name="facade_type_id"
              value={formData.facade_type_id}
              onChange={handleChange}
              options={options.facade_types.map(t => ({ value: t.id, label: t.name }))}
            />
            {selectedSaleType?.code !== 'new' && (
              <Input
                label="Bina Yaşı"
                name="building_age"
                type="number"
                value={formData.building_age}
                onChange={handleChange}
                placeholder="Örn: 5"
              />
            )}
          </div>
        </Card>

        <Card title="Konum Bilgileri" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="İl"
              name="city_id"
              value={formData.city_id}
              onChange={handleChange}
              options={options.cities.map(c => ({ value: c.id, label: c.name }))}
            />
            <Select
              label="İlçe"
              name="district_id"
              value={formData.district_id}
              onChange={handleChange}
              options={districts.map(d => ({ value: d.id, label: d.name }))}
            />
            <Select
              label="Mahalle"
              name="neighborhood_id"
              value={formData.neighborhood_id}
              onChange={handleChange}
              options={neighborhoods.map(n => ({ value: n.id, label: n.name }))}
            />
          </div>
          <Textarea
            label="Adres Detayı"
            name="address_detail"
            value={formData.address_detail}
            onChange={handleChange}
            placeholder="Detaylı adres bilgisi..."
            rows={3}
          />
        </Card>

        <Card title="Fiyat Bilgileri" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="İlk Fiyat (₺)"
              name="initial_price"
              type="number"
              value={formData.initial_price}
              onChange={handleChange}
              placeholder="Örn: 4800000"
            />
            <Input
              label="Liste Fiyatı (₺)"
              name="list_price"
              type="number"
              value={formData.list_price}
              onChange={handleChange}
              placeholder="Örn: 4500000"
              required
            />
            <Input
              label="Komisyon Oranı (%)"
              name="commission_rate"
              type="number"
              value={formData.commission_rate}
              onChange={handleChange}
              placeholder="Örn: 3"
            />
          </div>
        </Card>

        <Card title="Ek Bilgiler" className="mb-6">
          <RichTextEditor
            label="Açıklama"
            value={formData.additional_info}
            onChange={handleDescriptionChange}
            placeholder="Portföy hakkında detaylı bilgiler..."
          />
        </Card>

        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/portfolios')}
          >
            İptal
          </Button>
          <Button type="submit" disabled={loading || !canEdit}>
            {loading ? 'Kaydediliyor...' : isEditMode ? 'Portföyü Güncelle' : 'Portföyü Kaydet'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PortfolioForm;
